import { MultiLangString } from '@/models/general';
import { Address } from '@/models/address';
import { Money } from '@/models/money';
import { CreateFacility, Facility } from '@/models/facility';
import { SeoMetaData } from '@/models/seoMetaData';

export type LiftOperator = {
    id: string;
    name: MultiLangString;
    alternativeNames: string;
    active: boolean;
    payPerUse: boolean;
    phone: string;
    fax: string;
    homepage: string;
    email: string;
    shopEmail: string;
    address: Address;
    slopeKm: number;
    slopesBlue: number;
    slopesRed: number;
    slopesBlack: number;
    kmBlue: number;
    kmRed: number;
    kmBlack: number;
    routes: number;
    routesKm: number;
    draglifts: number;
    chairlifts: number;
    gondolas: number;
    aerialTramways: number;
    funiculars: number;
    conveyors: number;
    slopingTunnelRails: number;
    logos: {
        type: string;
        href: string;
    }[];
    facilities: (CreateFacility | Facility)[];
    pickupDepositAmount: Money;
    internalProductionDepositAmount: Money;
    internalProductionShippingCosts: Money;
    infoText: MultiLangString;
    covid19SkidataResort: string;
    dynamicPricingEnabled: boolean;
    dynamicPricingActive: boolean;
    mobileFlowEnabled: boolean;
    enableConfiguration: boolean;

    password: string;
    username: string;
    disablePortal: boolean;
    internalEmail: string;
    ticketBccEmail: string;
    poolMember: boolean;
    signDate: string;
    fastbillCustomerId: string;
    billingMode: 'CONTRACT_2020' | 'CONTRACT_2016';
    invoiceProviderName: string;
    maxTicketsPerStartDay: number;
    sixCredentials: {
        customerId: string;
        terminalId: string;
        username: string;
        password: string;
    };
    integration: 'NONE' | 'DTA' | 'BEI' | 'AXESS';
    incertActive: boolean;
    incertEndpoint: string;
    incertTerminalId: string;
    incertPassword: string;
    insuranceEnabled: boolean;
    disableB2b: boolean;
    disableHospitality: boolean;
    hideOriginalPrice: boolean;
    hideBarcode: boolean;
    numbirdsActive: boolean;
    covid19DeepLinkEnabled: boolean;
    metaData: SeoMetaData[];
    bei?: {
        username: string;
        password: string;
        pointOfSaleName: string;
        salesChannelShortName: string;
        environment: 'PROD' | 'STAGING';
    };
    dta?: {
        client: string;
        username: string;
        password: string;
        pointOfSaleName?: string;
        integrationClient?: string;
        integrationPointOfSaleName?: string;
        hospitalityClient?: string;
        environment: 'PROD' | 'STAGING';
    };
    axess?: {
        soapUrl: string;
        loginId: string;
        username: string;
        password: string;
        soapUsername: string;
        soapPassword: string;
    };
    smartPricer?: {
        environment: 'PRODUCTION' | 'DEVELOPMENT';
        customerId: string;
        apiToken: string;
        seasonId: string;
        profile: string;
    };
};

export type CreateLiftOperator = Omit<LiftOperator, 'id'>;

export const createNewLiftOperator = (): CreateLiftOperator => ({
    name: {
        de: '',
        en: '',
    },
    alternativeNames: '',
    active: false,
    payPerUse: false,
    insuranceEnabled: false,
    phone: '',
    fax: '',
    homepage: '',
    email: '',
    disableB2b: false,
    disableHospitality: false,
    shopEmail: '',
    address: {
        street: '',
        zipcode: '',
        city: '',
        country: 'AT',
        coordinates: {
            lat: 0,
            lng: 0,
        },
        viewportNE: {
            lat: 0,
            lng: 0,
        },
        viewportSW: {
            lat: 0,
            lng: 0,
        },
    },
    slopeKm: null,
    slopesBlue: null,
    slopesRed: null,
    slopesBlack: null,
    kmBlue: null,
    kmRed: null,
    kmBlack: null,
    routes: null,
    routesKm: null,
    draglifts: null,
    chairlifts: null,
    gondolas: null,
    aerialTramways: null,
    funiculars: null,
    conveyors: null,
    slopingTunnelRails: null,
    username: null,
    logos: [],
    facilities: [],
    pickupDepositAmount: {
        amount: 0,
        currency: 'EUR',
    },
    internalProductionDepositAmount: {
        amount: 0,
        currency: 'EUR',
    },
    internalProductionShippingCosts: {
        amount: 0,
        currency: 'EUR',
    },
    infoText: {
        de: '',
        en: '',
    },

    dynamicPricingEnabled: false,
    dynamicPricingActive: false,
    mobileFlowEnabled: false,
    enableConfiguration: false,

    disablePortal: false,
    internalEmail: '',
    ticketBccEmail: '',
    poolMember: false,
    signDate: '',
    fastbillCustomerId: '',
    billingMode: 'CONTRACT_2020',
    invoiceProviderName: '',

    maxTicketsPerStartDay: null,
    password: '',
    sixCredentials: {
        customerId: '',
        terminalId: '',
        username: '',
        password: '',
    },
    integration: 'NONE',

    incertActive: false,
    incertEndpoint: '',
    incertTerminalId: '',
    incertPassword: '',
    hideOriginalPrice: false,
    hideBarcode: false,
    numbirdsActive: false,
    covid19DeepLinkEnabled: false,
    covid19SkidataResort: '',
    metaData: [],
});

export const isOfTypeLiftOperator = (
    toBeDetermined: LiftOperator | CreateLiftOperator
): toBeDetermined is LiftOperator => {
    return (toBeDetermined as LiftOperator).id !== undefined;
};
